import React, { useState, useReducer, Dispatch } from 'react';
import { Validator, Input } from 'common/components';
import { NullishGuard } from 'hoc';
import { Layer } from 'layout';
import { debounce } from 'lodash';
import produce from 'immer';
import classNames from 'classnames';
import { findCustomer, api, process } from 'common/functions';
import { useLocation } from 'react-router-dom';
import type { FindType } from '../FindAccountPage';
import Result from './result/Result';
import './FindId.scss';

export type Action =
  | { type: 'loading'; payload: boolean }
  | { type: 'customer'; payload: Partial<{ name: string; error: string; id: number }> }
  | { type: 'user'; payload: Partial<{ name: string; email: string }> }
  | { type: 'result'; payload: { username: string; done: boolean } };

interface State {
  loading: boolean;
  type: '아이디 찾기' | '비밀번호 찾기';
  customer: { name: string; error: string; id: number };
  user: { name: string; email: string };
  result: { username: string; done: boolean };
}

const initialState: State = {
  loading: false,
  type: '아이디 찾기',
  customer: { name: '', error: '', id: -2 },
  user: { name: '', email: '' },
  result: { username: '', done: false },
};

const reducer = (state: State, action: Action) => {
  const { type, payload } = action;
  // prettier-ignore
  return produce(state, (draft) => {
    switch (type) {
      case 'loading': draft['loading'] = payload; break;
      case 'customer': draft['customer'] = { ...state.customer, ...payload }; break;
      case 'user': draft['user'] = { ...state.user, ...payload }; break;
      case 'result': draft['result'] = payload; break;
    }
  });
};

interface Props {
  children: React.ReactNode;
  setFindType: Dispatch<FindType>;
}

const FindId: React.FC<Props> = ({ children, setFindType }) => {
  const [state, setState] = useReducer(reducer, initialState);
  const { loading, customer, user, result } = state;
  const location = useLocation();
  const { pathname } = location;
  const isReviewer = pathname.includes('reviewer');

  const timer = () => {
    const info = async (name: string) => {
      const result = await findCustomer({ customer_name: name });
      if (result.id === -1) result.error = '등록되지 않은 고객사명입니다.';
      setState({ type: 'customer', payload: result });
    };
    return debounce(info, 500);
  };
  const [customerCheck] = useState(timer);

  const submitHandler = async () => {
    // 고객사가 존재할때
    if (customer.id > -1) {
      setState({ type: 'loading', payload: true });
      const params = {
        customer_id: customer.id,
        name: user.name,
        email: user.email,
        role: isReviewer ? 1 : 0,
      };
      process(
        api.reqData({ url: 'find/id', data: params }),
        api.post.request,
        api.fullFilled(({ response }) => {
          const payload = { username: '', done: true };
          if (response) payload.username = response.data.username;
          setState({ type: 'result', payload });
          setState({ type: 'loading', payload: false });
          setState({ type: 'user', payload: { name: '', email: '' } });
        })
      );
    }
  };

  return (
    <>
      <NullishGuard data={[result.done]}>
        <Result username={result.username} setState={setState} setFindType={setFindType} />
      </NullishGuard>

      <NullishGuard data={[!result.done]}>
        <Validator.Provider id="kyc-find-id-form-field" form onSubmit={submitHandler}>
          {children}
          <Layer styles={{ marginBottom: 16 }}>
            <Input
              className={classNames('basic', { error: customer.error, success: !customer.error && customer.id > -2 })}
              placeholder="고객사명을 입력하세요."
              validType="realtime"
              rules={['required']}
              onChange={(e) => {
                const name = e.target.value;
                setState({ type: 'customer', payload: { name, error: '', id: -2 } });
                if (name) {
                  customerCheck.cancel();
                  customerCheck(name);
                }
                if (!name.length) customerCheck.cancel();
              }}
            />
            {customer.error && (
              <div className={classNames('api-check-message', { error: customer.error })}>{customer.error}</div>
            )}
          </Layer>

          <Layer styles={{ marginBottom: 16 }}>
            <Input
              className="basic"
              placeholder="이름"
              validType="realtime"
              rules={['name', { maxLength: 100 }]}
              onChange={(e) => setState({ type: 'user', payload: { name: e.target.value } })}
            />
          </Layer>

          <Layer styles={{ marginBottom: 16 }}>
            <Input
              className="basic"
              placeholder="이메일"
              validType="realtime"
              rules={['email', { minLength: 6, maxLength: 250 }]}
              onChange={(e) => setState({ type: 'user', payload: { email: e.target.value } })}
            />
          </Layer>

          <Validator.Submit
            text="다음"
            disabled={state.customer.id < 0 || !state.user.name || !state.user.email}
            loading={loading}
            styles={{ width: '100%', height: 56, radius: 8 }}
          />
        </Validator.Provider>
      </NullishGuard>
    </>
  );
};

export default FindId;
